<template>
  <aside
    aria-live="polite"
    class="tw:flex tw:h-full tw:min-h-0 tw:w-full tw:flex-shrink-0 tw:flex-col tw:overflow-hidden tw:border-l tw:border-denim-200"
    data-testid="outreach-database-matches-panel"
  >
    <div
      class="tw:flex tw:items-center tw:justify-between tw:border-b tw:border-denim-200 tw:px-4 tw:py-3 tw:text-sm tw:font-semibold tw:text-denim-900"
    >
      <span>{{
        getLocaleText("modals.outreach_database_matches_panel.title")
      }}</span>
      <BaseButton
        :aria-label="getLocaleText('global.close')"
        circle
        icon="close"
        new-design
        new-icon
        @click="hideSideBar"
      />
    </div>

    <div class="tw:min-h-0 tw:flex-1 tw:overflow-auto tw:py-2">
      <div
        v-if="loading"
        class="tw:flex tw:justify-center tw:p-4"
      >
        <BaseSpinner class="tw:size-6" />
      </div>

      <p
        v-else-if="!matches.length"
        class="tw:px-2 tw:py-4 tw:text-sm tw:text-denim-600"
      >
        {{ getLocaleText("modals.outreach_database_matches_panel.no_matches") }}
      </p>

      <template v-else>
        <ul
          class="tw:m-0 tw:flex tw:list-none tw:flex-col tw:p-0"
          role="list"
        >
          <li
            v-for="match in matches"
            :key="String(match.id)"
            class="database-match tw:border-b tw:border-denim-100 tw:last:border-0"
            role="listitem"
          >
            <OutreachContact
              :contact="match"
              hide-checkbox
              hide-extras
              open-outlet-links-in-new-tab
              show-email
              @click="handlePreviewDatabaseMatch"
              @preview="handlePreviewDatabaseMatch"
            />
            <div class="database-match__actions">
              <OutreachContactActions
                :selected-ids="match.document_id ? [match.document_id] : []"
                @action="handleMatchAction"
              />
            </div>
          </li>
        </ul>
      </template>
    </div>

    <div
      v-if="pagination && pagination.pageCount > 1"
      class="tw:flex tw:flex-shrink-0 tw:justify-end tw:border-t tw:border-denim-200 tw:p-2"
    >
      <BasePagination
        class="tw:select-none"
        data-testid="database-matches-pagination"
        disable-boundary-numbers
        disable-ellipses
        hide-page-size-options
        :max-pages="2"
        :pagination="pagination"
        @pagination-changed="emit('pagination-changed', $event)"
      />
    </div>
  </aside>
</template>

<script setup lang="ts">
import { getLocaleText } from "shared/boot/i18n";
import { BaseButton, BaseSpinner } from "shared/components/base";
import BasePagination, {
  type BasePaginationData,
} from "shared/components/base/BasePagination";
import OutreachContact from "shared/components/Outreach/OutreachContact/OutreachContact.vue";
import OutreachContactActions, {
  Action,
} from "shared/components/Outreach/OutreachContactActions.vue";
import {
  type ContactResourceType,
  type ContactType,
} from "shared/composables/useContact";
import { useOutreachContactSidebar } from "shared/composables/useOutreachContactSidebar";
import ModalService from "shared/modals/ModalService";
import { useSideBarStore } from "shared/modules/SideBar/stores/useSideBarStore";

interface OutreachDatabaseMatchesPanelProps {
  loading?: boolean;
  matches: ContactType[];
  pagination?: BasePaginationData;
}

const props = defineProps<OutreachDatabaseMatchesPanelProps>();

const emit = defineEmits<{
  "pagination-changed": [pagination: BasePaginationData];
}>();

const { handlePreviewContact } = useOutreachContactSidebar();
const { hideSideBar } = useSideBarStore();

function handlePreviewDatabaseMatch(
  contact: ContactType,
  type: ContactResourceType
) {
  handlePreviewContact(contact, type, {
    loading: props.loading,
    matches: props.matches,
    source: "database_matches",
  });
}

function handleMatchAction(action: Action) {
  if ([Action.CREATE_MEDIA_RELEASE, Action.CREATE_PITCH].includes(action)) {
    ModalService.close("OutreachPrivateInfluencerModal");
  }
}
</script>

<style lang="scss" scoped>
:deep(.contact) {
  width: 100%;
}

:deep(.contact-details) {
  min-width: 0;
}

:deep(.contact-details-wrapper) {
  min-width: 0;
}

:deep(.contact-info) {
  min-width: 0;
}

.database-match :deep(.contact-header) {
  padding-right: 220px;
}

.database-match {
  position: relative;

  &__actions {
    opacity: 0;
    pointer-events: none;
    position: absolute;
    right: var(--s-spacing-lg);
    top: var(--s-spacing-lg);
    transition: opacity 150ms;
  }

  &:focus-within,
  &:hover {
    .database-match__actions {
      opacity: 1;
      pointer-events: auto;
    }
  }
}
</style>
